'use client'

import { MailIcon, PhoneIcon } from 'lucide-react'
import Link from 'next/link'
import React, { Fragment } from 'react'

import { NewsletterSignUpForm } from './NewsletterSignUpForm'
import {
  aboutUsLinkGroup,
  brandLinkGroup,
  footerUtilityLinks,
  helpSupportLinkGroup,
  loggedInAccountLinkGroup,
  loggedOutAccountLinkGroup,
  partnersLinkGroup,
  type SiteLinkGroup,
} from './siteLinks'
import { cn } from './ui/cn'
import { Wrapper } from './ui/Wrapper'

const Grid = Wrapper
const Col = Wrapper
const FinePrintBar = Wrapper
const LinkGroup = Wrapper
const NewsletterSignUp = Wrapper

type FooterColumnCount = 1 | 2 | 3

interface SiteFooterProps {
  className?: string
  logoUrl: string
  brandName: string
  email: { display: string; href: string }
  phone: { display: string; href: string }
  address: readonly string[]
  isLoggedIn?: boolean
  onLogIn?: () => void
  onLogOut?: () => void
  onSignUp?: () => void
}

function getFooterLinkColumns(
  isLoggedIn: boolean,
  columnCount: FooterColumnCount,
): SiteLinkGroup[][] {
  const accountLinkGroup = isLoggedIn ? loggedInAccountLinkGroup : loggedOutAccountLinkGroup

  if (columnCount === 1) {
    return [
      [brandLinkGroup, helpSupportLinkGroup, aboutUsLinkGroup, partnersLinkGroup, accountLinkGroup],
    ]
  }

  if (columnCount === 2) {
    return [
      [brandLinkGroup, aboutUsLinkGroup],
      [helpSupportLinkGroup, partnersLinkGroup, accountLinkGroup],
    ]
  }

  return [
    [brandLinkGroup],
    [helpSupportLinkGroup, partnersLinkGroup],
    [aboutUsLinkGroup, accountLinkGroup],
  ]
}

interface SocialLink {
  href: string
  src: string
  alt: string
  className: string
}

const socialLinks: SocialLink[] = [
  {
    href: 'https://www.linkedin.com/company/ship-play/',
    src: '/img/logos/linkedin.svg',
    alt: 'LinkedIn',
    className: 'snplay:size-8',
  },
  {
    href: 'https://www.facebook.com/shipandplay',
    src: '/img/logos/facebook.svg',
    alt: 'Facebook',
    className: 'snplay:size-8',
  },
  {
    href: 'https://youtube.com/@shipandplay',
    src: '/img/logos/youtube.svg',
    alt: 'YouTube',
    className: 'snplay:size-7',
  },
  {
    href: 'https://www.instagram.com/shipandplayofficial',
    src: '/img/logos/instagram.svg',
    alt: 'Instagram',
    className: 'snplay:size-7',
  },
  {
    href: 'https://x.com/shipandplay_',
    src: '/img/logos/x.svg',
    alt: 'X',
    className: 'snplay:size-5',
  },
  {
    href: 'https://www.tiktok.com/@shipandplay',
    src: '/img/logos/tiktok.svg',
    alt: 'TikTok',
    className: 'snplay:size-6',
  },
]

export const SiteFooter = ({
  className,
  logoUrl,
  brandName,
  email,
  phone,
  address,
  isLoggedIn = false,
  onLogIn,
  onLogOut,
  onSignUp,
}: SiteFooterProps): React.ReactElement => {
  const oneColumnLinkColumns = getFooterLinkColumns(isLoggedIn, 1)
  const twoColumnLinkColumns = getFooterLinkColumns(isLoggedIn, 2)
  const threeColumnLinkColumns = getFooterLinkColumns(isLoggedIn, 3)
  const copyrightText = `© Copyright ${new Date().getFullYear()} Ship&Play.`

  const handleAccountLinkAction = (action?: 'logIn' | 'logOut' | 'signUp'): void => {
    if (action === 'logIn') onLogIn?.()
    if (action === 'logOut') onLogOut?.()
    if (action === 'signUp') onSignUp?.()
  }

  const renderFooterLinkColumns = (columns: SiteLinkGroup[][]): React.ReactNode =>
    columns.map((column) => (
      <div className="snplay:space-y-8" key={column.map((group) => group.id).join('-')}>
        {column.map((group) => (
          <LinkGroup as="section" key={group.id}>
            <span className="snplay-group-name">
              {group.id === 'brand' ? brandName : group.title}
            </span>
            <ul className="snplay:space-y-1">
              {group.links.map((link) => {
                const LinkComponent = link.external ? 'a' : Link
                return (
                  <li key={link.label}>
                    <LinkComponent
                      href={link.url}
                      target={link.target}
                      className="snplay-footer-link snplay-can-focus"
                      onClick={(e) => {
                        if ('action' in link && link.action) {
                          e.preventDefault()
                          handleAccountLinkAction(link.action)
                        }
                      }}
                    >
                      {link.label}
                    </LinkComponent>
                  </li>
                )
              })}
            </ul>
          </LinkGroup>
        ))}
      </div>
    ))

  return (
    <div className={cn('snplay:bg-ui-primary snplay:text-ui-foreground', className)}>
      <footer className="snplay-bounds">
        <span className="snplay:mb-24 snplay:block snplay:h-2 snplay:w-full snplay:bg-primary">
          &nbsp;
        </span>
        <Grid className="snplay:grid-cols-12 snplay:gap-8 snplay:md:grid">
          <Col className="snplay:col-span-5 snplay:lg:col-span-4">
            <Link href="/" className="snplay-can-focus snplay:mb-6 snplay:inline-block">
              <img src={logoUrl} className="snplay:max-h-10" alt={brandName} />
            </Link>
            <p className="snplay:mb-4 snplay:text-base snplay:sm:text-sm">
              {address.map((line, i) => (
                <Fragment key={line}>
                  {line}
                  {i < address.length - 1 && <br />}
                </Fragment>
              ))}
            </p>
            <div
              className={cn(
                'snplay:mb-8 snplay:-ml-2 snplay:flex snplay:flex-wrap',
                'snplay:items-center snplay:gap-1',
              )}
            >
              {socialLinks.map((social) => (
                <a
                  key={social.alt}
                  href={social.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={social.alt}
                  className={cn(
                    'snplay-can-focus snplay:flex snplay:size-11',
                    'snplay:items-center snplay:justify-center',
                  )}
                >
                  <img src={social.src} alt={social.alt} className={social.className} />
                </a>
              ))}
            </div>
            {/* Was `sm:flex md:block` in playground (sm=640 there = tablets
                only). Kettle's sm=375 fired it on phones, cramming email and
                phone into a narrow flex row and wrapping the phone across three
                lines. Skip the intermediate flex tier — stack vertically. */}
            <ul className="snplay:mb-12 snplay:gap-12 snplay:space-y-2">
              <li>
                <a
                  href={email.href}
                  className={cn(
                    'snplay-footer-link snplay-can-focus snplay:inline-flex',
                    'snplay:items-center snplay:gap-4',
                  )}
                >
                  <MailIcon className="snplay:size-6 snplay:shrink-0 snplay:text-primary" />
                  {email.display}
                </a>
              </li>
              <li>
                <a
                  href={phone.href}
                  className={cn(
                    'snplay-footer-link snplay-can-focus snplay:inline-flex',
                    'snplay:items-center snplay:gap-4',
                    'snplay:whitespace-nowrap',
                  )}
                >
                  <PhoneIcon className="snplay:size-6 snplay:shrink-0 snplay:text-primary" />{' '}
                  {phone.display}
                </a>
              </li>
            </ul>
            <NewsletterSignUp>
              <span className="snplay-group-name snplay:mb-1 snplay:block snplay:text-primary">
                Newsletter
              </span>
              <p className="snplay:mb-6 snplay:text-balance snplay:text-base snplay:sm:text-sm">
                Sign up for our mailing list to get the latest updates and special offers.
              </p>
              <NewsletterSignUpForm />
            </NewsletterSignUp>
          </Col>
          <Col
            className={cn(
              'snplay:group snplay:col-span-6 snplay:col-start-7',
              'snplay:pt-16 snplay:max-xs:space-y-8',
              'snplay:lg:col-span-7 snplay:lg:col-start-6',
            )}
            data-bg="dark"
          >
            <div className="snplay:space-y-8 snplay:xs:hidden">
              {renderFooterLinkColumns(oneColumnLinkColumns)}
            </div>
            <div
              className={cn(
                'snplay:hidden snplay:gap-8 snplay:xs:grid',
                'snplay:xs:grid-cols-2 snplay:lg:hidden',
              )}
            >
              {renderFooterLinkColumns(twoColumnLinkColumns)}
            </div>
            <div className="snplay:hidden snplay:gap-8 snplay:lg:grid snplay:lg:grid-cols-3">
              {renderFooterLinkColumns(threeColumnLinkColumns)}
            </div>
          </Col>
        </Grid>
        <FinePrintBar
          className={cn(
            'snplay:mt-24 snplay:items-center snplay:justify-between snplay:gap-8',
            'snplay:border-t snplay:border-[#30465F] snplay:py-4',
            'snplay:text-base snplay:max-sm:text-center snplay:sm:text-sm snplay:lg:flex',
          )}
        >
          <div className="snplay:text-balance snplay:max-lg:my-4 snplay:max-lg:text-center">
            <span className="snplay:inline-block">{copyrightText}</span>{' '}
            <span className="snplay:inline-block">All rights reserved.</span>
          </div>
          <div
            className={cn(
              'snplay:flex snplay:flex-wrap snplay:items-center',
              'snplay:justify-center snplay:lg:justify-end',
            )}
          >
            {footerUtilityLinks.map((link) => (
              <Fragment key={link.label}>
                <Link
                  href={link.url}
                  target={link.target}
                  className={cn(
                    'snplay-footer-link snplay-can-focus',
                    'snplay:whitespace-nowrap snplay:text-base snplay:sm:text-sm',
                  )}
                >
                  {link.label}
                </Link>
                <span className="snplay:mx-3 snplay:text-ui-foreground/44">&bull;</span>
              </Fragment>
            ))}
            <span className="snplay:whitespace-nowrap snplay:text-primary">Version 2.0</span>
          </div>
        </FinePrintBar>
      </footer>
    </div>
  )
}
